import React, { useEffect, useRef, useState, useMemo } from 'react';
import extend from 'extend';
import { Box, styled } from '@mui/material';
import { stardust } from '@nebula.js/stardust';
import type { IListLayout } from '../hooks/types';
import type { IStores } from '../store';
import uid from '../utils/uid';
import useDirectQuery from '../hooks/direct-query/use-direct-query';

interface ListboxContainerProps {
  layout: IListLayout;
  model: EngineAPI.IGenericObject;
  borderBottom?: boolean;
  hasMultipleListboxes?: boolean;
  handleActive?: (id: string, active: boolean) => void;
  stores: IStores;
  closePopover?: () => void;
  isPopover?: boolean;
}

const StyledBox = styled(Box, { shouldForwardProp: (p: string) => !['showBorder', 'showBorderBottom'].includes(p) })(
  ({ showBorder, showBorderBottom }: { showBorder: boolean; showBorderBottom: boolean }) => ({
    height: '100%',
    border: showBorder ? '1px solid #d9d9d9' : '1px solid transparent',
    borderBottom: showBorder && showBorderBottom ? '1px solid #d9d9d9' : 0,
    borderRadius: '4px',
    overflow: 'hidden',
  }),
);

const ListboxContainer = ({
  layout,
  model,
  borderBottom,
  hasMultipleListboxes,
  handleActive,
  stores,
  closePopover,
  isPopover,
}: ListboxContainerProps) => {
  const [listboxInstance, setListboxInstance] = useState<stardust.FieldInstance>();
  const elRef = useRef<HTMLElement>();

  const { embed, constraints, options, renderTracker, directQueryEnabled, fpLayout } = stores.store.getState();

  const { components } = fpLayout || {};

  const dqOptionsOverrides = useDirectQuery({
    directQueryEnabled,
    layout,
    listBoxModel: model,
    constraints,
  });

  const isEditMode = !constraints?.edit;
  const showBorder = !isEditMode || (!!hasMultipleListboxes && !isPopover);

  const [key] = useState(uid());

  useEffect(() => {
    if (!layout || !embed) {
      return;
    }
    embed.field(layout.qInfo).then((inst: stardust.FieldInstance) => {
      setListboxInstance(inst);
    });
  }, []);

  const allowSelect = !constraints?.select && !constraints?.active;

  const listboxOptions = useMemo(
    () =>
      extend(
        true,
        {
          __DO_NOT_USE__: {
            selectDisabled: () => !allowSelect, // can we hook this into the selections api?
            focusSearch: isPopover,
          },
          direction: options?.direction,
          search: isPopover ? true : ('toggle' as stardust.SearchMode),
          isPopover,
          components,
          disablePortal: false,
        },
        dqOptionsOverrides || {},
      ),
    [allowSelect, isPopover, components, options?.direction],
  );

  useEffect(() => {
    if (listboxInstance) {
      // @ts-expect-error __DO_NOT_USE__
      // eslint-disable-next-line no-underscore-dangle
      listboxInstance.__DO_NOT_USE__?.options?.(listboxOptions);
    }
  }, [listboxOptions]);

  useEffect(() => {
    if (!elRef.current || !listboxInstance || !model) {
      return undefined;
    }

    listboxInstance.mount(elRef.current, listboxOptions).then(() => {
      renderTracker?.renderedCallback(key);
    });
    return () => {
      listboxInstance.unmount();
    };
  }, [elRef.current, listboxInstance, constraints, options?.direction]);

  useEffect(() => {
    if (!listboxInstance) {
      return undefined;
    }
    const handleActivate = () => {
      handleActive?.(layout.qInfo.qId, true);
    };
    const handleDeactivate = () => {
      handleActive?.(layout.qInfo.qId, false);
      closePopover?.();
    };
    listboxInstance.on?.('selectionActivated', handleActivate);
    listboxInstance.on?.('selectionDeactivated', handleDeactivate);

    return () => {
      listboxInstance.removeListener?.('selectionActivated', handleActivate);
      listboxInstance.removeListener?.('selectionDeactivated', handleDeactivate);
    };
  }, [listboxInstance]);

  return (
    <StyledBox
      showBorder={!!showBorder}
      showBorderBottom={borderBottom !== false}
      ref={elRef}
      data-testid="filterpane-listbox-container"
    />
  );
};

export default ListboxContainer;
